import type {
  BettingEvent,
  CampaignsType,
} from "hooks/firestore/v2/models/bettingEvent";
import type { RacingEvent } from "hooks/firestore/v2/models/racingEvent";
import type { GroupedTokens } from "hooks/firestore/v2/user/useTokens";
import type { Permissions } from "utilities/Auth/authSlice";
import { parseISO } from "date-fns";
import {
  getIsSGMReady,
  shouldShowPromoBanner,
} from "utilities/sharedBettingUtilities";
import type { Maybe } from "types/utilities";
import { parseDate } from "utilities/dateUtilities";
import type {
  ProcessedBettingEvent,
  ProcessedMarkets,
} from "@/hooks/firestore/v2/betting/useEvent";

const getMarketStatus = (
  status: Maybe<string>,
  bettingType: string,
): string => {
  if (!status) {
    return "ACTIVE";
  }

  if (status === "ACTIVE" && bettingType === "Live") {
    return "LIVE";
  }

  if (["ACTIVE", "SETTLED", "CANCELLED"].includes(status)) {
    return status;
  }

  return "SUSPENDED";
};

const processBettingEvent = (
  event: BettingEvent,
  userCampaigns: string[],
  userTokens: GroupedTokens,
  permissions: Permissions,
) => {
  const bettingType =
    event.eventType === "MATCH" && event.mainMarket
      ? event.mainMarket.bettingType
      : "Prematch";

  const semAvailableAtDate = event.attributes?.semAvailableAtTs?.toDate();

  const mainMarketNextBetStop =
    typeof event.mainMarket?.nextBetStop === "string"
      ? parseISO(event.mainMarket?.nextBetStop)
      : event.mainMarket?.nextBetStop?.toDate();

  const promotionCampaigns = event.promotionCampaigns?.filter(
    (campaign: CampaignsType) =>
      shouldShowPromoBanner(campaign, userCampaigns, userTokens, permissions),
  );

  const canViewPromo = permissions["viewPromotion"] === "GRANTED";

  return {
    ...event,
    isSGMReady: getIsSGMReady(semAvailableAtDate),
    attributes: {
      ...event.attributes,
      ...(semAvailableAtDate && {
        semAvailableAtTs: semAvailableAtDate,
      }),
    },
    mainMarket: event.mainMarket
      ? {
          ...event.mainMarket,
          bettingType,
          nextBetStop: mainMarketNextBetStop,
        }
      : undefined,
    scheduledStartTime: parseISO(event.scheduledStartTime),
    nextBetStop: event.nextBetStop.toDate(),
    status:
      event.eventType === "MATCH"
        ? getMarketStatus(event?.mainMarket?.status, bettingType)
        : "ACTIVE",
    hasPromotionalMarkets:
      canViewPromo &&
      (event.hasPromotionalMarkets || promotionCampaigns?.length > 0),
    promotionCampaigns: canViewPromo ? promotionCampaigns : [],
    showPromoBanner:
      canViewPromo &&
      (event.hasPromotionalMarkets || promotionCampaigns?.length > 0),
  };
};

const filterBettingMarkets = (
  market: ProcessedMarkets[0],
  permissions: Permissions,
  event: ProcessedBettingEvent,
): boolean => {
  // Exclude promo markets when user has no promo permissions
  if (
    market.hasPromotionalMarkets &&
    permissions["viewPromotion"] !== "GRANTED"
  ) {
    return false;
  }

  if (
    event.hub === "sports" &&
    permissions["viewSportsMarkets"] !== "GRANTED"
  ) {
    return false;
  }

  if (
    event.hub === "esports" &&
    permissions["viewEsportsMarkets"] !== "GRANTED"
  ) {
    return false;
  }

  if (
    Object.values(market.outcomes ?? {}).every(
      (outcome) => outcome.result === "VOID",
    )
  ) {
    return false;
  }

  if (
    ["sports", "esports"].includes(event.hub) &&
    Object.values(market.outcomes ?? {}).some(
      (outcome) =>
        outcome.active && outcome.odds === 0 && outcome.openingOdds === 0,
    )
  ) {
    return false;
  }

  if (event.mainMarket?.status === "ACTIVE") {
    return market.status !== "DEACTIVATED";
  }

  return true;
};

const processRacingEvent = (
  event: RacingEvent,
  userCampaigns: string[],
  userTokens: GroupedTokens,
  permissions: Permissions,
) => {
  const promotionCampaigns = event.promotionCampaigns?.filter(
    (campaign: CampaignsType) =>
      shouldShowPromoBanner(campaign, userCampaigns, userTokens, permissions),
  );

  const canViewPromo = permissions["viewPromotion"] === "GRANTED";

  return {
    ...event,
    hub: "racing",
    eventName: `${event.venue} - R${event.number}`,
    eventType: "RACE",
    scheduledStartTime: parseDate(event.scheduledStartTime),
    country: event.venueCountry,
    state: event.venueState,
    trackType: event.trackType,
    promotionCampaigns: canViewPromo ? promotionCampaigns : [],
    showPromoBanner:
      canViewPromo &&
      (event.hasPromotionalMarkets || promotionCampaigns?.length > 0),
  };
};

export {
  getMarketStatus,
  processBettingEvent,
  processRacingEvent,
  filterBettingMarkets,
};
